'use client';

import { useEffect, useState } from 'react';

import { useHeaderMenuContent } from '@/lib/context/HeaderMenuContentContext';
import { filterVisibleCountries } from '@/components/header/navShared';
import type { ContinentGroupDto, CountryDto } from '@/types/content';

/**
 * Continent → countries selection model shared by the desktop Destinations mega-menu
 * and the mobile Destinations panel. Keeps a single source of truth for filtering and
 * the "selected continent" behaviour so the two presentations never diverge.
 */
export function useDestinationsMenu() {
  const { content, contentLoading, contentError } = useHeaderMenuContent();
  const [selectedContinentId, setSelectedContinentId] = useState<number | null>(null);

  useEffect(() => {
    const first = content?.continentGroup?.[0];
    if (first) setSelectedContinentId(first.id);
  }, [content]);

  const continents: ContinentGroupDto[] = content?.continentGroup ?? [];
  const selectedContinent =
    continents.find((c) => c.id === selectedContinentId) ?? continents[0] ?? null;

  const countriesFromContinent = filterVisibleCountries(selectedContinent?.countries);
  const tourCountries = filterVisibleCountries(content?.tourCountries);

  const visibleCountries: CountryDto[] =
    continents.length > 0 ? countriesFromContinent : tourCountries;

  return {
    content,
    loading: contentLoading,
    loadError: contentError,
    continents,
    selectedContinent,
    selectedContinentId,
    setSelectedContinentId,
    countriesFromContinent,
    tourCountries,
    /** Countries to display for the current selection (falls back to flat tourCountries when no continents). */
    visibleCountries,
  };
}
